// src/features/sessions/editor/extensions/index.ts
import CharacterCount from '@tiptap/extension-character-count';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableRow } from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import TextAlign from '@tiptap/extension-text-align';
import Typography from '@tiptap/extension-typography';
import Underline from '@tiptap/extension-underline';
import { ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import type { EditorKind } from '../ClinicalEditorShell';
import { SlashCommand } from '../SlashCommand';
import { buildSlashItems } from '../slashMenuItems';

import { SlashList, type SlashListRef } from './slashCommandRenderer';

export const CHARACTER_LIMIT = 100_000;

// Inline-positioning fallback for the slash menu popup. tippy.js is not a
// dependency of this repo; rather than introduce one for v1, we anchor the
// renderer element to the caret rect via fixed positioning. This keeps the
// renderer self-contained and avoids the multi-attempt typecheck risk flagged
// in the D4 dispatch.
//
// Flip-up logic: the rendered menu is `max-h-80` (320px) inside `slashCommandRenderer`.
// When the caret is in the lower half of the viewport, anchoring below would
// clip the menu against the editor footer / browser chrome. Measure the
// element after it's been mounted to the DOM and re-position above the caret
// when there isn't enough room below.
function positionElement(element: HTMLElement, rect: DOMRect): void {
  const margin = 6;
  element.style.position = 'fixed';
  element.style.left = `${String(Math.round(rect.left))}px`;
  element.style.zIndex = '50';

  const viewportH = globalThis.window.innerHeight;
  // Read the rendered height after the element is in the DOM. On the very
  // first call inside `onStart` (before append), offsetHeight is 0 and we
  // fall back to the configured `max-h-80` ceiling so the flip decision still
  // happens. After the menu is appended, subsequent updates measure correctly.
  const measuredH = element.offsetHeight;
  const menuH = measuredH > 0 ? measuredH : 320;

  const spaceBelow = viewportH - rect.bottom;
  const spaceAbove = rect.top;
  const shouldFlipUp = spaceBelow < menuH + margin && spaceAbove > spaceBelow;

  if (shouldFlipUp) {
    element.style.top = `${String(Math.round(rect.top - menuH - margin))}px`;
  } else {
    element.style.top = `${String(Math.round(rect.bottom + margin))}px`;
  }
}

/**
 * Shared TipTap extension list for both Notes and Case Report editors.
 * Bubble-menu and slash-command extensions are wired separately at the shell
 * level (they require renderer functions provided at component-mount time).
 */
export function buildEditorExtensions(placeholder: string, kind: EditorKind) {
  const allItems = buildSlashItems(kind);
  return [
    StarterKit.configure({
      // bullet/ordered list, blockquote, code, heading, horizontal rule, history all enabled by default
      heading: { levels: [1, 2, 3] },
      // Added below with our own config; StarterKit v3 bundles both too.
      link: false,
      underline: false,
    }),
    CharacterCount.configure({ limit: CHARACTER_LIMIT }),
    Placeholder.configure({ placeholder }),
    TaskList,
    TaskItem.configure({ nested: true }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
    }),
    Underline,
    // Smart typography — em-dashes, ellipses, smart quotes — so the prose
    // reads like editorial copy rather than a textarea. No config needed; the
    // defaults are exactly what a clinician would expect when typing "..." or
    // "--" mid-sentence.
    Typography,
    // Clinical highlighter — multiple colors so practitioners can mark risk
    // language (yellow) vs. quoted client speech (mint) vs. follow-ups (rose).
    Highlight.configure({ multicolor: true }),
    // Text alignment for headings + paragraphs only. Lists/blockquotes keep
    // their natural left alignment — aligning a list center reads as broken.
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    // Tables — added so AI-drafted NDJSON `table` blocks (session details,
    // MSE domains, risk factor grids) render as real tables instead of
    // collapsing to concatenated text. `resizable: false` keeps the
    // edit surface predictable; column widths follow content.
    Table.configure({ resizable: false, HTMLAttributes: { class: 'editor-table' } }),
    TableRow,
    TableHeader,
    TableCell,
    SlashCommand.configure({
      suggestion: {
        items: () => allItems,
        render: () => {
          let component: ReactRenderer<SlashListRef> | null = null;
          let host: HTMLDivElement | null = null;
          return {
            onStart: (props) => {
              component = new ReactRenderer(SlashList, {
                props: { ...props, allItems },
                editor: props.editor,
              });
              const rect = props.clientRect?.();
              if (rect === null || rect === undefined) return;
              host = globalThis.document.createElement('div');
              host.appendChild(component.element);
              // Append first so positionElement can measure offsetHeight on
              // the next call. The initial fallback (320px) handles the
              // pre-measure frame; we re-run after the DOM commit so the
              // flip-up math uses the real rendered height.
              globalThis.document.body.appendChild(host);
              positionElement(host, rect);
              globalThis.requestAnimationFrame(() => {
                if (host !== null) positionElement(host, rect);
              });
            },
            onUpdate: (props) => {
              component?.updateProps({ ...props, allItems });
              const rect = props.clientRect?.();
              if (rect === null || rect === undefined || host === null) return;
              positionElement(host, rect);
            },
            onKeyDown: (props) => {
              if (props.event.key === 'Escape') {
                if (host !== null) {
                  host.style.display = 'none';
                }
                return true;
              }
              return component?.ref?.onKeyDown(props) ?? false;
            },
            onExit: () => {
              host?.remove();
              host = null;
              component?.destroy();
              component = null;
            },
          };
        },
      },
    }),
  ];
}
